/**
 * Temporary screen-scoped design tokens for **Sign Request Detail**
 * (`/requests/view/:id`).
 *
 * Mirrors `Sign Request Detail.dc.html` 1:1 — every colour, size, radius,
 * spacing and type value the screen needs lives here so the components never
 * inline a literal. Once every screen is re-skinned these get promoted into
 * `src/theme` and this file is deleted (see UI_IMPLEMENTATION_RULES.md
 * § Temporary Screen Tokens).
 */

import { button, contentCard, inkfree, menu, notification, signer, statusChip, white } from "theme/colors";

/** Chrome shared by every white card on the screen. */
const card = {
  bg: contentCard.bg,
  border: contentCard.border,
  radius: "16px",
  shadow: contentCard.shadow,
  /** Divider under a card header and between list rows. */
  divider: signer.border.divider,
  /** Row hover wash. */
  hoverBg: signer.surface.hover,
} as const;

/** Outlined button chrome (Send Reminder, Preview, audit footer, kebab). */
const outlined = {
  bg: white,
  color: signer.text.secondary,
  border: button.secondaryBorderWarm,
  hoverBorder: signer.ink,
  hoverColor: signer.ink,
} as const;

export const viewSignRequestTokens = {
  card,
  outlined,

  /** Content column wrapping the whole screen. */
  page: {
    paddingTop: "28px",
    paddingX: "40px",
    paddingBottom: "56px",
    /** Left column | right rail. */
    gridTemplateColumns: "minmax(0,1fr) 372px",
    columnGap: "22px",
    sectionGap: "22px",
  },

  /** ← Sign Requests › Employment Agreement — SoftSages */
  breadcrumb: {
    gap: "8px",
    marginBottom: "18px",
    fontSize: "13.5px",
    fontWeight: 600,
    linkColor: signer.text.subtitle,
    linkHoverColor: signer.orange.main,
    activeColor: signer.ink,
    activeFontWeight: 700,
    backIconSize: 15,
    separatorColor: signer.border.strong,
    separatorSize: 13,
    activeMaxWidth: 520,
  },

  /** Title / status / meta / actions card at the top of the page. */
  header: {
    padding: "24px 28px",
    marginBottom: "22px",
    rowGap: "18px",
    iconTileSize: 52,
    iconTileRadius: "13px",
    iconGlyphSize: 24,
    titleGap: "12px",
    titleFontSize: "21px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.01em",
    titleColor: signer.ink,
    metaGap: "20px",
    metaMarginTop: "10px",
    metaFontSize: "13px",
    metaColor: signer.text.subtitle,
    metaIconSize: 15,
    metaIconColor: signer.text.faint,
    metaStrongColor: signer.text.secondary,
    metaStrongWeight: 700,
    actionsGap: "10px",
  },

  /** Status chip shared by the header and every recipient row. */
  statusChip: {
    gap: "7px",
    padding: "5px 12px",
    rowPadding: "5px 11px",
    radius: "999px",
    dotSize: 7,
    fontSize: "12px",
    fontWeight: 700,
  },

  /** Filled "Download Documents". */
  primaryButton: {
    bg: inkfree.main,
    hoverBg: inkfree.dark,
    color: white,
    height: 44,
    padding: "0 18px",
    radius: "10px",
    fontSize: "13.5px",
    fontWeight: 700,
    gap: "9px",
    iconSize: 16,
  },

  /** Outlined "Send Reminder" + the 44px kebab beside it. */
  secondaryButton: {
    height: 44,
    padding: "0 16px",
    radius: "10px",
    fontSize: "13.5px",
    fontWeight: 700,
    gap: "8px",
    iconSize: 16,
    kebabSize: 44,
    kebabIconSize: 18,
    kebabColor: signer.text.subtitle,
  },

  /**
   * Dropdown opened by the header kebab. The design uses label-only rows — no
   * leading icons — inside a soft-shadowed card whose own 6px padding lets each
   * row show its own rounded hover wash.
   */
  menu: {
    paperBg: white,
    paperBorder: menu.border,
    paperRadius: "12px",
    paperShadow: "0 12px 34px rgba(33,21,71,.18)",
    paperPadding: "6px",
    paperMinWidth: 210,
    /** Gap between the 44px kebab and the paper (design: top 50px). */
    paperOffsetTop: "6px",
    itemPadding: "10px 13px",
    itemRadius: "8px",
    itemFontSize: "13px",
    itemFontWeight: 600,
    itemColor: signer.text.secondary,
    itemHoverBg: signer.surface.muted,
    /** Space before the trailing feature-gate lock. */
    itemGap: "10px",
    /** Void / Delete. */
    destructiveColor: notification.error.icon,
    /** Feature-gated rows stay legible instead of MUI's .38 opacity wash. */
    disabledColor: button.disabledColor,
    lockIconSize: 14,
  },

  /** SIGNING PROGRESS ▸ bar ▸ "4 of 4 completed". */
  progress: {
    gap: "16px",
    marginTop: "20px",
    paddingTop: "18px",
    labelFontSize: "12px",
    labelFontWeight: 700,
    labelLetterSpacing: ".08em",
    labelColor: signer.text.faint,
    barHeight: 7,
    barRadius: "4px",
    barTrack: signer.border.light,
    valueFontSize: "12.5px",
    valueFontWeight: 700,
    valueColor: signer.text.secondary,
  },

  /** Completion / declined strip under the header card. */
  banner: {
    gap: "14px",
    padding: "16px 20px",
    radius: "14px",
    marginBottom: "22px",
    iconCircleSize: 38,
    iconGlyphSize: 19,
    titleFontSize: "14px",
    titleFontWeight: 800,
    subFontSize: "12.5px",
    subColor: signer.text.meta,
    subMarginTop: "2px",
    buttonHeight: 40,
    buttonPadding: "0 16px",
    buttonRadius: "10px",
    buttonFontSize: "13px",
    buttonFontWeight: 700,
    successBg: notification.success.bg,
    successBorder: "#BFE4CF",
    successColor: notification.success.title,
    successIcon: notification.success.icon,
    successHoverBorder: notification.success.icon,
    errorBg: notification.error.bg,
    errorBorder: "#F0C9C9",
    errorColor: notification.error.title,
    errorIcon: notification.error.icon,
    errorHoverBorder: notification.error.icon,
  },

  /** Documents / Approvers / Signers card headers. */
  sectionHeader: {
    padding: "18px 24px",
    gap: "10px",
    titleFontSize: "15px",
    titleFontWeight: 800,
    titleColor: signer.ink,
    countMinWidth: 22,
    countHeight: 22,
    countPadding: "0 7px",
    countRadius: "11px",
    countFontSize: "11.5px",
    countFontWeight: 700,
    countBg: signer.surface.pill,
    countColor: signer.text.meta,
    /** Approvers use the violet count chip. */
    countApproverBg: signer.purple.chipBg,
    countApproverColor: "#5B4490",
    badgeGap: "7px",
    badgeFontSize: "12px",
    badgeFontWeight: 700,
    badgeColor: signer.text.faint,
    badgeIconSize: 14,
    toggleSize: 32,
    toggleRadius: "8px",
    toggleColor: signer.text.subtitle,
    toggleIconSize: 16,
    toggleHoverBg: signer.border.divider,
    toggleHoverColor: signer.ink,
  },

  /** One file inside the Documents card. */
  documentRow: {
    padding: "15px 24px",
    gap: "14px",
    thumbWidth: 42,
    thumbHeight: 52,
    thumbRadius: "7px",
    thumbBg: signer.orange.tint,
    thumbBorder: "#F5DECC",
    thumbGlyphSize: 19,
    thumbGlyphColor: signer.orange.main,
    nameFontSize: "14px",
    nameFontWeight: 700,
    nameColor: signer.ink,
    metaFontSize: "12px",
    metaColor: signer.text.faint,
    metaMarginTop: "3px",
    actionHeight: 36,
    actionPadding: "0 13px",
    actionRadius: "9px",
    actionFontSize: "12.5px",
    actionFontWeight: 700,
    actionGap: "7px",
    actionIconSize: 14,
    iconButtonSize: 36,
    iconButtonGlyphSize: 15,
  },

  /** "1 · LEVEL 1 · SIGNS IN ANY ORDER ————" strip above each signer group. */
  levelHeader: {
    padding: "15px 24px 5px",
    gap: "10px",
    numMinWidth: 24,
    numHeight: 24,
    numPadding: "0 8px",
    numRadius: "12px",
    numFontSize: "11px",
    numFontWeight: 800,
    numBg: signer.surface.pill,
    numColor: signer.text.meta,
    numCcBg: notification.warning.bg,
    numCcColor: notification.warning.icon,
    labelFontSize: "11px",
    labelFontWeight: 800,
    labelLetterSpacing: ".1em",
    labelColor: signer.text.faint,
    parallelFontSize: "10.5px",
    parallelFontWeight: 800,
    parallelLetterSpacing: ".06em",
    parallelColor: "#1B5DAC",
    parallelBg: "#EAF2FC",
    parallelRadius: "6px",
    parallelPadding: "3px 8px",
    ruleColor: signer.border.divider,
  },

  /** One signer / approver / CC recipient row. */
  recipientRow: {
    /** Signers sit under a level strip, so they are indented to clear it. */
    padding: "13px 24px 13px 58px",
    /** Approvers have no level strip. */
    flatPadding: "15px 24px",
    gap: "16px",
    avatarSize: 42,
    avatarFontSize: "14px",
    avatarFontWeight: 700,
    avatarColor: white,
    nameGap: "9px",
    nameFontSize: "14px",
    nameFontWeight: 700,
    nameColor: signer.ink,
    roleFontSize: "10.5px",
    roleFontWeight: 800,
    roleLetterSpacing: ".06em",
    roleRadius: "6px",
    rolePadding: "3px 7px",
    roleCcColor: notification.warning.icon,
    roleCcBg: notification.warning.bg,
    emailFontSize: "12.5px",
    emailColor: signer.text.faint,
    emailMarginTop: "3px",
    metaGap: "14px",
    metaMarginTop: "6px",
    metaFontSize: "11.5px",
    metaColor: signer.text.faint, // content, not a disabled control (disabledColor is 2.06:1)
    metaIconSize: 12,
    noteMarginTop: "10px",
    noteBg: notification.error.bg,
    noteBorder: "#F0C9C9",
    noteRadius: "9px",
    notePadding: "10px 13px",
    noteGap: "9px",
    noteIconSize: 14,
    noteIconColor: notification.error.icon,
    noteFontSize: "12.5px",
    noteLineHeight: 1.6,
    noteColor: notification.error.title,
    kebabSize: 32,
    kebabRadius: "8px",
    kebabColor: signer.text.subtitle,
    kebabIconSize: 17,
    kebabHoverBg: signer.border.divider,
    kebabHoverColor: signer.ink,
  },

  /** Message-to-signers card. */
  message: {
    padding: "20px 24px",
    titleFontSize: "15px",
    titleFontWeight: 800,
    titleColor: signer.ink,
    titleMarginBottom: "10px",
    bodyFontSize: "13.5px",
    bodyLineHeight: 1.7,
    bodyColor: signer.text.meta,
    bodyBg: signer.surface.hover,
    bodyBorder: signer.border.divider,
    bodyRadius: "11px",
    bodyPadding: "14px 16px",
  },

  /** Right-rail audit trail. */
  audit: {
    headerPadding: "18px 22px",
    headerGap: "12px",
    iconTileSize: 36,
    iconTileRadius: "9px",
    iconTileBg: signer.orange.tint,
    iconGlyphSize: 17,
    iconGlyphColor: signer.orange.main,
    titleGap: "8px",
    titleFontSize: "15px",
    titleFontWeight: 800,
    titleColor: signer.ink,
    subFontSize: "12px",
    subColor: signer.text.faint,
    subMarginTop: "1px",
    bodyPadding: "20px 22px 8px",
    bodyMaxHeight: 560,
    eventGap: "14px",
    railSize: 32,
    connectorWidth: "2px",
    connectorMinHeight: 20,
    connectorColor: signer.border.light,
    contentPaddingBottom: "22px",
    actorFontSize: "13px",
    actorFontWeight: 700,
    actorColor: signer.ink,
    textFontSize: "12.5px",
    textColor: signer.text.meta,
    textLineHeight: 1.55,
    textMarginTop: "2px",
    whenFontSize: "11.5px",
    whenColor: signer.text.faint, // content, not a disabled control (disabledColor is 2.06:1)
    whenMarginTop: "4px",
    footerPadding: "0 22px 18px",
    footerButtonHeight: 40,
    footerButtonRadius: "10px",
    footerButtonFontSize: "12.5px",
    footerButtonFontWeight: 700,
    /** Device line under an event's timestamp: small glyph + one truncated line. */
    deviceGap: "5px",
    deviceIconSize: 13,
    deviceMarginTop: "3px",
    /**
     * Tamper-evidence bar at the foot of the card. Idle (not yet checked) sits on
     * the neutral surface; a result takes the matching status-chip tone so it
     * reads like every other status in the app.
     */
    integrityPadding: "10px 12px",
    integrityRadius: "10px",
    integrityGap: "10px",
    integrityIconSize: 20,
    integrityTitleFontSize: "12.5px",
    integrityTitleFontWeight: 700,
    integrityDetailFontSize: "11.5px",
    integrityDetailMarginTop: "1px",
    integrityActionFontSize: "12px",
    integrityActionFontWeight: 700,
    integrityIdle: {
      bg: signer.surface.hover,
      border: signer.border.divider,
      text: signer.ink,
      icon: signer.text.faint,
    },
    integrityTone: statusChip.tone,
    /**
     * A long trail collapses to its newest and oldest events with an inline
     * "show more" disc between them; below the threshold everything is shown.
     */
    collapseThreshold: 6,
    headCount: 2,
    tailCount: 2,
    ellipsisBg: signer.surface.pill,
    ellipsisColor: signer.text.subtitle,
  },

  /** Right-rail request summary. */
  details: {
    padding: "20px 22px",
    titleFontSize: "15px",
    titleFontWeight: 800,
    titleColor: signer.ink,
    titleMarginBottom: "16px",
    rowGap: "13px",
    fontSize: "13px",
    labelColor: signer.text.faint,
    labelFontWeight: 600,
    valueColor: signer.text.secondary,
    valueFontWeight: 700,
    valueAccentColor: signer.orange.main,
    emptyValue: "—",
  },
} as const;

/**
 * Per-status palette driving the header chip, the header document tile, the
 * progress fill and every recipient chip. Keyed by the design's own status
 * vocabulary; `getViewRequestStatusKey` maps the app's `status` strings onto it.
 */
export const viewSignRequestStatusTokens = {
  pending: {
    label: "Pending",
    bg: notification.warning.bg,
    dot: "#E0A82E",
    color: notification.warning.icon,
  },
  progress: {
    label: "In progress",
    bg: "#EAF2FC",
    dot: "#2A7DE1",
    color: "#1B5DAC",
  },
  completed: {
    label: "Completed",
    bg: notification.success.bg,
    dot: notification.success.icon,
    color: notification.success.title,
  },
  declined: {
    label: "Declined",
    bg: notification.error.bg,
    dot: notification.error.icon,
    color: notification.error.title,
  },
  neutral: {
    label: "On hold",
    bg: signer.surface.pill,
    dot: signer.text.faint,
    color: signer.text.meta,
  },
} as const;

export type ViewSignRequestStatusKey = keyof typeof viewSignRequestStatusTokens;

/**
 * Compact scales for `Sign Request Detail - Mobile & Tablet.dc.html`.
 *
 * Every entry is a `[phone 375, phone 390, tablet 834]` triple. The desktop
 * value is **not** repeated here — `ViewSignRequest.styles.ts` appends it from
 * `viewSignRequestTokens` above, so the ≥900px rendering stays byte-for-byte
 * what it was before the responsive pass.
 *
 * The reference draws 375 and 390 as two distinct columns (they differ by
 * ~1-2px on nearly every value), so both are kept rather than collapsed.
 */
export const viewSignRequestCompactTokens = {
  /** Card radius shrinks one step off desktop's 16px. */
  cardRadius: ["15px", "15px", "16px"],

  page: {
    padX: ["14px", "16px", "26px"],
    padTop: ["14px", "16px", "22px"],
    padBottom: ["32px", "34px", "40px"],
    /** Gap between the stacked cards / between the two tablet columns. */
    gap: ["14px", "15px", "18px"],
    /** Cancels the layout's own top margin so the bar meets the app nav. */
    layoutTopGutter: ["-16px", "-16px", "-32px"],
    /** Cancels `Container`'s side gutters so the bar can go full bleed. */
    layoutSideGutter: ["-16px", "-16px", "-24px"],
  },

  /** Sticky "← Sign Requests ⋮" strip that replaces the breadcrumb below md. */
  bar: {
    /** Sits directly under the app's 64px sticky TopNav. */
    stickyTop: 64,
    height: ["auto", "auto", 60],
    padY: ["12px", "12px", "0px"],
    padX: ["14px", "16px", "24px"],
    gap: ["11px", "11px", "14px"],
    buttonSize: 38,
    buttonRadius: "11px",
    backIconSize: 19,
    /** Pulls the 38px hit area back so the glyph lines up with the content. */
    backOffset: ["-6px", "-6px", "-8px"],
    titleFontSize: ["14px", "14.5px", "15px"],
    titleFontWeight: 700,
    titleColor: signer.ink,
    separatorSize: 13,
    separatorColor: signer.border.strong,
    currentMaxWidth: 320,
    kebabIconSize: 18,
    kebabColor: signer.text.subtitle,
    kebabHoverBg: signer.border.divider,
    kebabHoverColor: signer.ink,
    backHoverBg: signer.surface.muted,
    borderColor: signer.border.light,
  },

  header: {
    padding: ["16px", "17px", "22px 24px"],
    /** Space between the sticky bar and the header card. */
    marginTop: ["14px", "16px", "22px"],
    marginBottom: ["14px", "15px", "18px"],
    rowGap: ["12px", "13px", "16px"],
    iconTileSize: [42, 44, 48],
    iconTileRadius: ["11px", "12px", "12px"],
    iconGlyphSize: [19, 20, 22],
    titleFontSize: ["14.5px", "15px", "18px"],
    titleLineHeight: [1.35, 1.35, "normal"],
    /** Phone drops the chip onto its own line under the title block. */
    chipMarginTop: ["11px", "12px", 0],
    metaMarginTop: ["12px", "12px", "9px"],
    metaGap: ["6px", "6px", "16px"],
    metaFontSize: ["11.5px", "12px", "12.5px"],
    actionsMarginTop: ["14px", "15px", 0],
    actionsGap: ["9px", "9px", "9px"],
    buttonHeight: [42, 44, 40],
    buttonFontSize: ["13px", "13.5px", "12.5px"],
    buttonIconSize: [15, 16, 15],
    primaryPadding: ["0px", "0px", "0 15px"],
    secondaryPadding: ["0px", "0px", "0 14px"],
  },

  statusChip: {
    padding: ["5px 11px", "5px 12px", "5px 12px"],
    rowPadding: ["4px 9px", "4px 10px", "4px 10px"],
    dotSize: [6, 6, 7],
    rowDotSize: [6, 6, 6],
    fontSize: ["11.5px", "12px", "12px"],
    rowFontSize: ["10.5px", "11px", "11px"],
  },

  progress: {
    marginTop: ["14px", "15px", "18px"],
    paddingTop: ["13px", "14px", "16px"],
    gap: ["11px", "12px", "14px"],
    labelFontSize: ["10px", "10.5px", "11px"],
    barHeight: [6, 6, 7],
    barRadius: ["3px", "3px", "4px"],
    valueFontSize: ["11px", "11.5px", "12px"],
    /** Phone shortens "SIGNING PROGRESS" to fit beside the bar. */
    label: ["PROGRESS", "PROGRESS", "SIGNING PROGRESS"],
  },

  banner: {
    padding: ["14px 16px", "15px 17px", "15px 18px"],
    marginBottom: ["14px", "15px", "18px"],
    /** Phone stacks the copy above a full-width button. */
    direction: ["column", "column", "row"],
    gap: ["11px", "12px", "14px"],
    innerGap: ["11px", "12px", "14px"],
    iconCircleSize: [34, 36, 36],
    titleFontSize: ["13px", "13.5px", "13.5px"],
    subFontSize: ["11.5px", "12px", "12px"],
    subMarginTop: ["3px", "3px", "2px"],
    subLineHeight: [1.5, 1.5, "normal"],
    buttonHeight: [38, 40, 38],
    buttonPadding: ["0px", "0px", "0 15px"],
    buttonRadius: ["9px", "9px", "9px"],
    buttonFontSize: ["12.5px", "13px", "12.5px"],
    buttonWidth: ["100%", "100%", "auto"],
    /** Tablet has no room for the full label. */
    buttonLabel: ["Download Certificate", "Download Certificate", "Certificate"],
  },

  sectionHeader: {
    padding: ["14px 16px", "15px 17px", "16px 20px"],
    gap: ["9px", "9px", "9px"],
    titleFontSize: ["14px", "14.5px", "14.5px"],
    countMinWidth: [20, 20, 20],
    countHeight: [20, 20, 20],
    countPadding: ["0 6px", "0 6px", "0 6px"],
    countRadius: ["10px", "10px", "10px"],
    countFontSize: ["11px", "11px", "11px"],
    /** A 32px hit area would add 12px to the 48px header the design draws. */
    toggleSize: [20, 20, 20],
    toggleIconSize: [15, 15, 15],
  },

  /** "SIGNS IN ORDER · 2 LEVELS", dropped below the Signers header. */
  orderBadge: {
    padding: ["0 16px 4px", "0 17px 5px", "0 20px 6px"],
    fontSize: ["10.5px", "11px", "11px"],
  },

  documentRow: {
    padding: ["12px 16px", "13px 17px", "13px 20px"],
    gap: ["11px", "12px", "13px"],
    thumbWidth: [36, 38, 38],
    thumbHeight: [44, 46, 46],
    thumbRadius: ["6px", "6px", "6px"],
    thumbGlyphSize: [16, 17, 17],
    nameFontSize: ["12.5px", "13px", "13px"],
    metaFontSize: ["11px", "11.5px", "11.5px"],
    metaMarginTop: ["2px", "2px", "2px"],
    iconButtonSize: [32, 34, 34],
    iconButtonRadius: ["8px", "9px", "9px"],
    iconButtonGlyphSize: [14, 14, 14],
  },

  levelHeader: {
    padding: ["11px 16px 4px", "12px 17px 4px", "13px 20px 4px"],
    gap: ["8px", "8px", "8px"],
    numMinWidth: [20, 20, 20],
    numHeight: [20, 20, 20],
    numPadding: ["0 6px", "0 6px", "0 6px"],
    numRadius: ["10px", "10px", "10px"],
    numFontSize: ["10px", "10px", "10px"],
    labelFontSize: ["10px", "10.5px", "10.5px"],
  },

  recipientRow: {
    padding: [
      "11px 16px 11px 30px",
      "12px 17px 12px 32px",
      "13px 20px 13px 36px",
    ],
    flatPadding: ["13px 16px", "14px 17px", "14px 20px"],
    gap: ["11px", "12px", "13px"],
    avatarSize: [36, 38, 38],
    avatarFontSize: ["12px", "12.5px", "12.5px"],
    nameGap: ["7px", "7px", "7px"],
    nameFontSize: ["13px", "13.5px", "13.5px"],
    roleFontSize: ["9.5px", "10px", "10px"],
    rolePadding: ["2px 6px", "2px 6px", "2px 6px"],
    roleRadius: ["5px", "5px", "5px"],
    emailFontSize: ["11px", "11.5px", "11.5px"],
    emailMarginTop: ["2px", "2px", "2px"],
    metaFontSize: ["10.5px", "11px", "11px"],
    metaMarginTop: ["5px", "5px", "5px"],
    noteMarginTop: ["8px", "9px", "9px"],
    notePadding: ["9px 11px", "10px 12px", "10px 12px"],
    noteRadius: ["8px", "8px", "8px"],
    noteFontSize: ["11.5px", "12px", "12px"],
  },

  message: {
    padding: ["15px 16px", "16px 17px", "17px 20px"],
    titleFontSize: ["14px", "14.5px", "14.5px"],
    titleMarginBottom: ["9px", "9px", "10px"],
    bodyFontSize: ["12.5px", "13px", "13px"],
    bodyLineHeight: [1.6, 1.6, 1.65],
    bodyPadding: ["12px 13px", "13px 14px", "13px 15px"],
    bodyRadius: ["10px", "10px", "11px"],
  },

  audit: {
    headerPadding: ["14px 16px", "15px 17px", "16px 20px"],
    headerGap: ["10px", "10px", "11px"],
    iconTileSize: [32, 34, 34],
    iconGlyphSize: [15, 16, 16],
    titleFontSize: ["14px", "14.5px", "14.5px"],
    bodyPadding: ["15px 16px 2px", "16px 17px 2px", "17px 20px 2px"],
    eventGap: ["11px", "12px", "12px"],
    railSize: [26, 28, 28],
    connectorMinHeight: [16, 18, 18],
    contentPaddingBottom: ["18px", "19px", "19px"],
    actorFontSize: ["12px", "12.5px", "12.5px"],
    textFontSize: ["11.5px", "12px", "12px"],
    textLineHeight: [1.5, 1.5, 1.5],
    whenFontSize: ["10.5px", "11px", "11px"],
    whenMarginTop: ["3px", "3px", "3px"],
    footerPadding: ["0 16px 15px", "0 17px 16px", "0 20px 17px"],
    footerButtonHeight: [38, 40, 40],
    footerButtonRadius: ["9px", "9px", "9px"],
    footerButtonFontSize: ["12px", "12.5px", "12.5px"],
  },
} as const;

export default viewSignRequestTokens;
